import { Fragment, useState } from 'react';
import type { PreviousInspection } from '../api/schemas';
import { GradeBadge } from './GradeBadge';

interface Props {
  rows: PreviousInspection[];
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

export function PreviousInspectionsTable({ rows }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-600">No previous inspections on record.</p>;
  }

  return (
    <>
      {/* Mobile: card per inspection */}
      <ul className="sm:hidden divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden">
        {rows.map((row, idx) => {
          const isOpen = expanded === idx;
          return (
            <Fragment key={`m-${row.inspection_date ?? ''}-${idx}`}>
              <li>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                    isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {fmtDate(row.inspection_date)}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      {row.inspection_type ?? '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <GradeBadge code={row.inspection_grade} size="sm" />
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                        (row.inspection_demerits ?? 0) === 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {row.inspection_demerits ?? 0}
                    </span>
                    <span className="text-xs text-snhd-primary font-medium">
                      {isOpen ? 'Hide' : 'View'}
                    </span>
                  </div>
                </button>
              </li>
              {isOpen && (
                <li className="bg-gray-50 px-6 py-4">
                  {row.violations_resolved.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      No violations were recorded for this inspection.
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
                      {row.violations_resolved.map((v) => (
                        <li key={v.id} className="px-4 py-3 text-sm text-gray-700">
                          {v.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
            </Fragment>
          );
        })}
      </ul>

      {/* Tablet+ : original table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <Th>Date</Th>
              <Th align="center">Grade</Th>
              <Th align="center">Demerits</Th>
              <Th>Type</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, idx) => {
              const isOpen = expanded === idx;
              return (
                <Fragment key={`d-${row.inspection_date ?? ''}-${idx}`}>
                  <tr
                    onClick={() => setExpanded(isOpen ? null : idx)}
                    className={`cursor-pointer transition-colors ${
                      isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {fmtDate(row.inspection_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <GradeBadge code={row.inspection_grade} size="sm" />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                          (row.inspection_demerits ?? 0) === 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {row.inspection_demerits ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {row.inspection_type ?? '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded(isOpen ? null : idx);
                        }}
                        className="text-snhd-primary hover:text-snhd-primary-hover text-sm font-medium"
                      >
                        {isOpen ? 'Hide' : 'View Violations'}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50">
                      <td colSpan={5} className="px-6 py-4">
                        {row.violations_resolved.length === 0 ? (
                          <p className="text-sm text-gray-600">
                            No violations were recorded for this inspection.
                          </p>
                        ) : (
                          <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
                            {row.violations_resolved.map((v) => (
                              <li key={v.id} className="px-4 py-3 text-sm text-gray-700">
                                {v.description}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      scope="col"
      className={`px-4 py-3 text-${align} text-xs font-medium text-gray-700 uppercase tracking-wider`}
    >
      {children}
    </th>
  );
}
